import moment from "moment";
import { TagColor } from "../../../app/data/common/tagColor";
import {
  AnalyticsGranularity,
  DenimUploadBatchItemStatus,
  DenimUploadBatchStatus,
  DocumentGapBucket,
  FactoredInvoiceDocumentStatus,
  FactoredInvoiceStatus,
  FactoringAttemptDocumentOutcome,
  FactoringAttemptOutcome,
  FactoringAttemptStep,
  FactoringDocumentType,
  FactoringIssueType,
  FactoringNotificationEvent,
  InvoiceListView,
} from "../../../app/data/invoice-factoring/models";

export const FACTORING_PAGE_SIZE = 50;
export const FACTORING_ITEMS_PAGE_SIZE = 100;
export const FACTORING_FILTER_DEBOUNCE_MS = 500;
export const FACTORING_POLL_MS = 5000;
export const FACTORING_MAX_FILE_BYTES = 5 * 1024 * 1024;

/** Batch and invoice data changes while batches run, so always refetch on mount. */
export const FACTORING_QUERY_OPTIONS = {
  staleTime: 0,
  refetchOnMount: "always" as const,
  refetchOnWindowFocus: false,
};

export const ACTIVE_BATCH_STATUSES: DenimUploadBatchStatus[] = ["QUEUED", "RUNNING", "STOP_REQUESTED"];

export const BATCH_STATUS_LABELS: Record<DenimUploadBatchStatus, string> = {
  DRAFT: "Not started",
  QUEUED: "Queued",
  RUNNING: "Sending",
  STOP_REQUESTED: "Stopping",
  STOPPED: "Stopped",
  COMPLETED: "Completed",
  COMPLETED_WITH_ERRORS: "Completed with errors",
  FAILED: "Failed",
};

export const BATCH_STATUS_COLORS: Record<DenimUploadBatchStatus, TagColor> = {
  DRAFT: TagColor.GREY,
  QUEUED: TagColor.BLUE,
  RUNNING: TagColor.BLUE,
  STOP_REQUESTED: TagColor.YELLOW,
  STOPPED: TagColor.ORANGE,
  COMPLETED: TagColor.GREEN,
  COMPLETED_WITH_ERRORS: TagColor.YELLOW,
  FAILED: TagColor.RED,
};

export const ITEM_STATUS_LABELS: Record<DenimUploadBatchItemStatus, string> = {
  PENDING: "Waiting",
  PROCESSING: "Sending",
  COMPLETED: "Sent",
  SKIPPED: "Already sent",
  FAILED: "Failed",
  INVALID: "Invalid row",
  DUPLICATE_IN_FILE: "Duplicate in file",
};

export const ITEM_STATUS_COLORS: Record<DenimUploadBatchItemStatus, TagColor> = {
  PENDING: TagColor.GREY,
  PROCESSING: TagColor.BLUE,
  COMPLETED: TagColor.GREEN,
  SKIPPED: TagColor.PURPLE,
  FAILED: TagColor.RED,
  INVALID: TagColor.ORANGE,
  DUPLICATE_IN_FILE: TagColor.ORANGE,
};

export const INVOICE_STATUS_LABELS: Record<FactoredInvoiceStatus, string> = {
  PENDING: "Not sent",
  JOB_CREATED: "Job created",
  DOCUMENTS_SENT: "Documents sent",
  NEEDS_ATTENTION: "Needs attention",
};

export const INVOICE_STATUS_COLORS: Record<FactoredInvoiceStatus, TagColor> = {
  PENDING: TagColor.GREY,
  JOB_CREATED: TagColor.BLUE,
  DOCUMENTS_SENT: TagColor.GREEN,
  NEEDS_ATTENTION: TagColor.RED,
};

export const ATTEMPT_STEP_LABELS: Record<FactoringAttemptStep, string> = {
  JOB_CREATION: "Job creation",
  DOCUMENT_UPLOAD: "Document upload",
};

export const ATTEMPT_OUTCOME_LABELS: Record<FactoringAttemptOutcome, string> = {
  IN_PROGRESS: "In progress",
  SUCCESS: "Sent",
  SKIPPED_ALREADY_SENT: "Skipped, already sent",
  SKIPPED_NO_DOCUMENTS: "Skipped, no documents",
  DENIM_DUPLICATE: "Duplicate on Denim",
  FAILED: "Failed",
};

export const ATTEMPT_OUTCOME_COLORS: Record<FactoringAttemptOutcome, TagColor> = {
  IN_PROGRESS: TagColor.BLUE,
  SUCCESS: TagColor.GREEN,
  SKIPPED_ALREADY_SENT: TagColor.PURPLE,
  SKIPPED_NO_DOCUMENTS: TagColor.ORANGE,
  DENIM_DUPLICATE: TagColor.ORANGE,
  FAILED: TagColor.RED,
};

export const ATTEMPT_DOCUMENT_OUTCOME_LABELS: Record<FactoringAttemptDocumentOutcome, string> = {
  SENT: "Sent",
  SKIPPED_ALREADY_SENT: "Skipped, already sent",
  FAILED: "Failed",
};

export const ATTEMPT_DOCUMENT_OUTCOME_COLORS: Record<FactoringAttemptDocumentOutcome, TagColor> = {
  SENT: TagColor.GREEN,
  SKIPPED_ALREADY_SENT: TagColor.PURPLE,
  FAILED: TagColor.RED,
};

export const DOCUMENT_STATUS_LABELS: Record<FactoredInvoiceDocumentStatus, string> = {
  NOT_SENT: "Not sent",
  SENT: "Sent",
  FAILED: "Failed",
};

export const DOCUMENT_STATUS_COLORS: Record<FactoredInvoiceDocumentStatus, TagColor> = {
  NOT_SENT: TagColor.GREY,
  SENT: TagColor.GREEN,
  FAILED: TagColor.RED,
};

export const DOCUMENT_TYPE_LABELS: Record<FactoringDocumentType, string> = {
  INVOICE: "Invoice",
  BILL_OF_LADING: "Bill of lading",
  PROOF_OF_DELIVERY: "Proof of delivery",
  WEIGHT_AND_INSPECTION: "Weight and inspection",
};

export const NOTIFICATION_EVENT_LABELS: Record<FactoringNotificationEvent, string> = {
  BATCH_STARTED: "Batch started",
  BATCH_COMPLETED: "Batch completed",
  BATCH_COMPLETED_WITH_ERRORS: "Batch completed with errors",
  BATCH_STOPPED: "Batch stopped",
  BATCH_FAILED: "Batch failed",
};

export const ISSUE_TYPE_LABELS: Record<FactoringIssueType, string> = {
  JOB_FAILED: "Job creation failed",
  DENIM_DUPLICATE: "Duplicate on Denim",
  DOCUMENTS_FAILED: "Document upload failed",
  NO_PROOF_OF_DELIVERY: "No proof of delivery yet",
};

export const DOCUMENT_GAP_LABELS: Record<DocumentGapBucket, string> = {
  UNDER_1_DAY: "Under 1 day",
  "1_TO_3_DAYS": "1 to 3 days",
  "3_TO_7_DAYS": "3 to 7 days",
  OVER_7_DAYS: "Over 7 days",
};

export const INVOICE_VIEW_LABELS: Record<InvoiceListView, string> = {
  needsAttention: "Needs attention",
  documentGaps: "Document gaps",
};

export const GRANULARITY_LABELS: Record<AnalyticsGranularity, string> = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
};

export const CSV_TEMPLATE = "probill_number\n12345678\n";

export const formatDateTime = (value?: string | null) => (value ? value.toUiDateTimeFormat() : "—");

/** Bill dates are date-only values stored at UTC midnight. */
export const formatBillDate = (value?: string | null) => (value ? moment.utc(value).format("MM/DD/YYYY") : "—");

export const formatCents = (cents?: number | null) =>
  cents === null || cents === undefined
    ? "—"
    : (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

export const formatJson = (value: unknown) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
};

export const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;
